import { useCallback, useEffect, useState } from "react";
import {
  SequenceService,
  type CatalogTouchOut,
  type SequenceDetailOut,
  type SenderIdentity,
  type SequenceStage,
} from "@services/sequence/sequenceService";
import { ctaFromBody } from "src/modules/engagement/components/phases/emailSms/touchCopy";
import type { Sequence, Touch } from "src/modules/engagement/components/phases/emailSms/emailSmsTypes";

const service = new SequenceService();

/** A catalog playbook, plus the count the catalog's "Used in" line shows. */
export type CatalogSequence = Sequence & { usedIn: number };

/*
    The catalog, read from the server (MPN-2201).

    It used to render four invented playbooks held in a TSX file, so nothing an Ops user did
    here reached anything and the page agreed with no campaign. Rows now, and the catalog is
    as full as the catalog is -- which until MPN-2209 loads the stage playbooks is one
    published playbook. That is the right answer rather than a reason to keep the fixtures.
*/
export function useCatalog() {
  const [sequences, setSequences] = useState<CatalogSequence[]>([]);
  const [stages, setStages] = useState<SequenceStage[]>([]);
  const [preview, setPreview] = useState<SenderIdentity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // Detail per playbook, because the list carries no touches and the catalog lists
      // MESSAGES. The catalog is tens of playbooks, not thousands.
      const rows = await service.listCatalogSequences();
      const detail = await Promise.all(
        rows.map((row) => service.getCatalogSequence(row.id)),
      );
      setSequences(detail.map(toCatalogSequence));
      setError(null);
    } catch {
      setError("Could not load the catalog. Try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    service.listSequenceStages().then(setStages).catch(() => undefined);
    service.getCatalogPreviewIdentity().then(setPreview).catch(() => undefined);
  }, [load]);

  const editTouch = useCallback(
    async (sequenceId: number, touchId: number, patch: Record<string, string>) => {
      setError(null);
      try {
        await service.editCatalogTouch(sequenceId, touchId, patch);
        await load();
        return true;
      } catch {
        setError("Could not save this message. Nothing was changed — try again.");
        return false;
      }
    },
    [load],
  );

  const setPublished = useCallback(
    async (sequenceId: number, published: boolean) => {
      setError(null);
      try {
        await service.setCatalogSequencePublished(sequenceId, published);
        await load();
        return true;
      } catch (e: unknown) {
        // The server refuses an empty playbook with a sentence worth repeating, rather than
        // a generic failure: "a playbook with no messages cannot be published" is the fix.
        const message = e instanceof Error ? e.message : "";
        setError(message || "Could not change this playbook. Try again.");
        return false;
      }
    },
    [load],
  );

  return { sequences, stages, preview, isLoading, error, reload: load, editTouch, setPublished };
}

/** The catalog's own touch shape. No campaign laid over it, so no overrides and no approval. */
function toCatalogTouch(touch: CatalogTouchOut, published: boolean): Touch {
  return {
    id: `touch-${touch.id}`,
    ch: touch.channel as Touch["ch"],
    title: touch.title,
    days: touch.offset_days,
    dir: touch.direction as Touch["dir"],
    anchor: touch.key_date_code,
    hr: touch.send_hour,
    grp: touch.group_id == null ? "main" : String(touch.group_id),
    on: true,
    // A touch is as published as the playbook holding it: the catalog has no per-message
    // publish, and the campaign screen's gate is the playbook's status.
    pub: published,
    ver: touch.version,
    subject: touch.subject,
    pre: touch.preheader,
    body: touch.html,
    text: touch.text,
    cta: ctaFromBody(touch.html).cta ?? "",
  };
}

export function toCatalogSequence(row: SequenceDetailOut): CatalogSequence {
  const published = row.status === "published";
  return {
    usedIn: row.used_in ?? 0,
    id: String(row.id),
    name: row.name,
    stage: row.stage ?? "",
    target: row.target_action,
    win: row.window,
    starter: "",
    published,
    groups: row.groups.map((g) => ({ id: String(g.id), name: g.name, note: g.note })),
    emailCount: row.touches.filter((t) => t.channel === "email").length,
    smsCount: row.touches.filter((t) => t.channel === "sms").length,
    touches: row.touches.map((t) => toCatalogTouch(t, published)),
  };
}
